<script setup lang="ts">
import { ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import AppButton from '@uiKit/elements/Button.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Text from '@uiKit/elements/Text.vue'
import AmountInputField from './AmountInputField.vue'

const DEFAULT_DEPOSIT_AMOUNT = 5000

const emit = defineEmits<{
  (e: 'continue', amount: number): void
  (e: 'cancel'): void
}>()

const amount = ref(DEFAULT_DEPOSIT_AMOUNT)
const amountError = ref('')

watch(amount, () => {
  if (amountError.value) amountError.value = ''
})

function formatAmount(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

const handleContinue = () => {
  if (amount.value <= 0) {
    amountError.value = 'Enter an amount greater than AED 0.'
    return
  }
  amountError.value = ''
  emit('continue', amount.value)
}
</script>

<template>
  <Box direction="col" gap="4" class="df">
    <Box direction="col" gap="2" class="df__body">
      <AmountInputField v-model="amount" />

      <Box v-if="amountError" align="center" gap="1" class="df__field-error">
        <PiIcon name="exclamation-circle" class="df__field-error-icon" />
        <Text type="caption" color="danger" is="span">{{ amountError }}</Text>
      </Box>

      <Box direction="col" gap="2" class="df__breakdown">
        <Box align="center" justify="between" gap="3">
          <Text type="body" color="muted" is="span">Deposit amount</Text>
          <Text type="body" weight="medium" is="span">AED {{ formatAmount(amount) }}</Text>
        </Box>
        <Box align="center" justify="between" gap="3">
          <Text type="body" color="muted" is="span">Processing fee</Text>
          <Text type="body" weight="medium" color="success" is="span">Free</Text>
        </Box>
        <Box class="df__breakdown-divider" />
        <Box align="center" justify="between" gap="3">
          <Text type="subheading" is="span">You'll transfer</Text>
          <Text type="subheading" weight="bold" is="span">AED {{ formatAmount(amount) }}</Text>
        </Box>
      </Box>
    </Box>

    <Box class="df__spacer" />

    <Box direction="col" gap="3" class="df__actions">
      <AppButton label="Continue" block size="large" @click="handleContinue" />
      <AppButton label="Cancel" variant="text" color="secondary" block @click="emit('cancel')" />
    </Box>

    <Box align="center" justify="center" gap="2" class="df__trust">
      <PiIcon name="shield" style="font-size: 14px; color: var(--p-success-600)" />
      <Text type="caption" color="muted" is="span">
        Funds held in escrow with Emirates NBD &middot; VARA-compliant
      </Text>
    </Box>
  </Box>
</template>

<style scoped>
.df {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 1.25rem 1.5rem 1.5rem;
  box-sizing: border-box;
}

.df__body {
  flex-shrink: 0;
}

.df__breakdown {
  background: var(--p-surface-50);
  border-radius: var(--p-border-radius-md);
  padding: 0.875rem 1rem;
  margin-top: 0.5rem;
}

.df__breakdown-divider {
  height: 1px;
  background: var(--p-surface-200);
  margin: 0.25rem 0;
}

.df__field-error {
  margin-top: 0.125rem;
}

.df__field-error-icon {
  color: var(--p-danger-600);
  font-size: 12px;
}

.df__spacer {
  flex: 1;
  min-height: 1rem;
}

.df__actions {
  flex-shrink: 0;
}

.df__trust {
  flex-shrink: 0;
  text-align: center;
}
</style>
